import React, { FC, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  Chip,
  TablePagination,
  Divider,
} from '@material-ui/core';
import { Search, GetApp, Add, Remove, Edit, Sync } from '@material-ui/icons';
import * as XLSX from 'xlsx';
import { useStyles } from './styles';

interface TimelineProps {
  events?: any;
  loading?: boolean;
  userName?: string;
  userEmail?: string;
  header?: string;
}

const Timeline: FC<TimelineProps> = ({ events = [], loading = false, userName, userEmail }) => {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const timelineEvents = useMemo(() => {
    if (Array.isArray(events)) return events;
    if (Array.isArray(events?.timeline)) return events.timeline;
    return [];
  }, [events]);

  const getDate = (event: any): Date | null => {
    const value = event.created_at || event.createdAt || event.createtimestamp;

    if (!value) return null;

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  const getStatus = (event: any) => {
    const title = String(event.title || '').toLowerCase();
    const type = String(event.type || '').toLowerCase();

    if (/deleted|removed|remove/.test(title + type)) return 'removed';

    if (/created|added|assigned/.test(title + type)) return 'created';

    if (/sync/.test(title + type)) return 'synced';

    return 'updated';
  };

  const actors = useMemo(() => {
    return Array.from(new Set(timelineEvents.map((e: any) => e.created_by_name).filter(Boolean))) as string[];
  }, [timelineEvents]);

  const filteredEvents = useMemo(() => {
    const now = new Date();

    return timelineEvents
      .filter((event: any) => {
        const query = search.toLowerCase();

        const searchable = [event.title, event.raw_description, event.created_by_name, event.type]
          .join(' ')
          .toLowerCase();

        if (!searchable.includes(query)) return false;

        const status = getStatus(event);

        if (typeFilter !== 'all' && status !== typeFilter) return false;

        if (actorFilter !== 'all' && event.created_by_name !== actorFilter) return false;

        if (dateFilter !== 'all') {
          const date = getDate(event);
          if (!date) return false;

          const start = new Date(now);
          start.setHours(0, 0, 0, 0);

          if (dateFilter === 'today' && date < start) return false;

          if (dateFilter === '7days') {
            start.setDate(start.getDate() - 6);
            if (date < start) return false;
          }

          if (dateFilter === '30days') {
            start.setDate(start.getDate() - 29);
            if (date < start) return false;
          }
        }

        return true;
      })
      .sort((a: any, b: any) => (getDate(b)?.getTime() || 0) - (getDate(a)?.getTime() || 0));
  }, [timelineEvents, search, typeFilter, dateFilter, actorFilter]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEvents.slice(start, start + rowsPerPage);
  }, [filteredEvents, page, rowsPerPage]);

  const groups = useMemo(() => {
    const result: Record<string, any[]> = {};

    paginated.forEach((event: any) => {
      const date = getDate(event);
      const key = date
        ? date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        : 'Unknown date';

      if (!result[key]) result[key] = [];
      result[key].push(event);
    });

    return Object.entries(result);
  }, [paginated]);

  const exportExcel = () => {
    const rows = filteredEvents.map((event: any) => ({
      Event: event.title || '',
      Description: event.raw_description || '',
      Actor: event.created_by_name || '',
      Status: getStatus(event),
      Date: getDate(event)?.toLocaleString() || '',
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(book, sheet, 'Timeline');
    XLSX.writeFile(book, 'EasyIdentity_Timeline.xlsx');
  };

  const getIcon = (status: string) => {
    switch (status) {
      case 'created':
        return <Add fontSize='small' />;
      case 'removed':
        return <Remove fontSize='small' />;
      case 'synced':
        return <Sync fontSize='small' />;
      default:
        return <Edit fontSize='small' />;
    }
  };

  const getChanges = (event: any) => {
    // Adjust this mapping to match your API's change structure.
    const changes = event.changes || event.details?.changes;

    if (Array.isArray(changes)) return changes;

    if (changes && typeof changes === 'object') {
      return Object.entries(changes).map(([field, value]: any) => ({
        field,
        previous: value?.old ?? value?.previous,
        current: value?.new ?? value?.current,
      }));
    }

    return [];
  };

  const formatValue = (value: any) => {
    if (value === undefined || value === null) return 'Not recorded';

    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
  };

  return (
    <Paper className={classes.timelinePaper}>
      <Box className={classes.timelineHeader}>
        <Box display='flex' alignItems='center' style={{ gap: 12 }}>
          <Typography className={classes.heading}>Timeline</Typography>
          <Typography className={classes.eventCount}>{filteredEvents.length} events</Typography>
        </Box>

        <Button
          variant='contained'
          className={classes.exportButton}
          startIcon={<GetApp />}
          onClick={exportExcel}
          disabled={!filteredEvents.length}
        >
          EXPORT
        </Button>
      </Box>

      <Box className={classes.filterRow}>
        <TextField
          variant='outlined'
          size='small'
          placeholder='Search timeline...'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
        />

        <FormControl variant='outlined' size='small'>
          <Select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(String(e.target.value));
              setPage(0);
            }}
          >
            <MenuItem value='all'>All event types</MenuItem>
            <MenuItem value='created'>Created</MenuItem>
            <MenuItem value='updated'>Updated</MenuItem>
            <MenuItem value='removed'>Removed</MenuItem>
            <MenuItem value='synced'>Synced</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant='outlined' size='small'>
          <Select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(String(e.target.value));
              setPage(0);
            }}
          >
            <MenuItem value='all'>All dates</MenuItem>
            <MenuItem value='today'>Today</MenuItem>
            <MenuItem value='7days'>Last 7 days</MenuItem>
            <MenuItem value='30days'>Last 30 days</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant='outlined' size='small'>
          <Select
            value={actorFilter}
            onChange={(e) => {
              setActorFilter(String(e.target.value));
              setPage(0);
            }}
          >
            <MenuItem value='all'>All actors</MenuItem>
            {actors.map((actor) => (
              <MenuItem key={actor} value={actor}>
                {actor}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className={classes.filterSummary}>
        <Typography variant='caption'>Showing {filteredEvents.length} events · Newest first</Typography>
        <Typography variant='caption'>Times in account timezone</Typography>
      </Box>

      <Divider />

      <Box className={classes.timelineBody}>
        {loading ? (
          <Typography>Loading timeline...</Typography>
        ) : filteredEvents.length === 0 ? (
          <Box className={classes.emptyState}>No timeline events found</Box>
        ) : (
          groups.map(([date, dateEvents]) => (
            <Box key={date} className={classes.dateGroup}>
              <Typography className={classes.dateHeading}>{date}</Typography>

              {dateEvents.map((event: any, index: number) => {
                const id = String(event.id ?? `${date}-${index}`);

                const status = getStatus(event);
                const eventDate = getDate(event);
                const isExpanded = !!expanded[id];
                const changes = getChanges(event);

                return (
                  <Box key={id} className={classes.eventRow}>
                    <Box className={classes.iconColumn}>
                      <Avatar className={`${classes.eventAvatar} ${status === 'removed' ? classes.removedAvatar : ''}`}>
                        {getIcon(status)}
                      </Avatar>
                    </Box>

                    <Box className={classes.eventMain}>
                      <Typography className={classes.eventTitle}>{event.title || 'Activity updated'}</Typography>

                      <Typography className={classes.description}>
                        {event.raw_description || 'No fields changed'}
                      </Typography>

                      <Typography className={classes.eventMeta}>
                        By <span className={classes.blueLink}>{event.created_by_name || 'Unknown User'}</span>
                        {' · '}
                        {eventDate
                          ? eventDate.toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })
                          : 'Unknown time'}
                      </Typography>

                      {isExpanded && (
                        <Box className={classes.detailsPanel}>
                          {changes.length > 0 ? (
                            <table className={classes.changesTable}>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Previous value</th>
                                  <th>New value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {changes.map((change: any, i: number) => (
                                  <tr key={i}>
                                    <td>{change.field || change.name}</td>
                                    <td>{formatValue(change.previous ?? change.old)}</td>
                                    <td>{formatValue(change.current ?? change.new)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <Typography variant='caption'>
                              Historical field changes are not available for this event.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box className={classes.eventActions}>
                      <Chip size='small' label={status} className={classes.statusChip} />

                      <Button
                        size='small'
                        className={classes.detailsButton}
                        onClick={() =>
                          setExpanded((prev) => ({
                            ...prev,
                            [id]: !prev[id],
                          }))
                        }
                      >
                        {isExpanded ? 'Hide details' : 'View details'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Box className={classes.pagination}>
        <TablePagination
          component='div'
          count={filteredEvents.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={(_: any, next: number) => setPage(next)}
          onChangeRowsPerPage={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
    </Paper>
  );
};

export default Timeline;
